import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Container,
  Navbar,
  NavbarBrand,
  NavbarToggler,
  Nav,
  NavItem,
  NavLink,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap'
import Link from 'next/link'
import Notifications from '~/components/shared/Notifications'
import PersonCircle from '~/components/shared/PersonCircle'
import { SUPPORT_APP_IDS } from '~/lib/constants/apps'
import { SITE_NAME } from '~/lib/constants/company'
import OutageNotifications from '~/components/shared/OutageNotifications'
import { checkScope } from '~/lib/helpers/checkScope'

function NavToggler ({ toggleSideNav }) {
  return (
    <NavbarToggler
      onClick={toggleSideNav} tag={({ className, onClick }) => (
        <div className={`cursor-pointer d-md-none ${className}`} onClick={onClick}>
          <i className='fa fa-menu fa-lg' />
        </div>
      )}
    />
  )
}

NavToggler.propTypes = {
  toggleSideNav: PropTypes.func.isRequired
}

function NavImageLink ({ children }) {
  return (
    <Link href='/'>
      <a>{children}</a>
    </Link>
  )
}

NavImageLink.propTypes = {
  children: PropTypes.node.isRequired
}

function NavImage () {
  return (
    <NavbarBrand tag='div'>
      <img className='p-0' src='/static/img/logo.png' alt={SITE_NAME} />
    </NavbarBrand>
  )
}

function NavMain ({ url, profile }) {
  const { pathname } = url

  return (
    <Nav className='nav-main ml-auto d-none d-md-flex'>
      <MainNavLink href='/' name='Home' pathname={pathname} />
      <MainNavLink href='/news' name='News' pathname={pathname} />
      <MainNavLink href='/resources' name='Resources' pathname={pathname} />
      <MainNavLink href='/reports' name='Reports' pathname={pathname} />
      <SupportNavDropdown pathname={pathname} profile={profile} />
    </Nav>
  )
}

NavMain.propTypes = {
  url: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired
}

function MainNavLink ({ href, name, pathname }) {
  const active = (name === 'Home' && pathname === '/') || pathname.startsWith(`/${name.toLowerCase()}`)

  return (
    <NavItem>
      <Link href={href} passHref>
        <NavLink active={active}>{name}</NavLink>
      </Link>
    </NavItem>
  )
}

MainNavLink.propTypes = {
  href: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  pathname: PropTypes.string.isRequired
}

function SupportNavDropdownApps ({ profile }) {
  const appMap = {}
  for (const app of profile.user.applications) {
    appMap[app.id] = app
  }

  const apps = SUPPORT_APP_IDS.map(id => {
    const app = appMap[id]

    if (app) {
      return <DropdownItem key={app.id} tag='a' href={app.embedUrl} target='_blank' rel='noopener noreferrer'>{app.name}</DropdownItem>
    } else {
      return null
    }
  })

  return apps
}

SupportNavDropdownApps.propTypes = {
  profile: PropTypes.object.isRequired
}

function SupportNavDropdown ({ pathname, profile }) {
  const activeClass = pathname.startsWith('/support') ? 'active' : ''
  const origamiPermission = useMemo(() => checkScope(profile, 'support.origami.view'))

  return (
    <UncontrolledDropdown nav inNavbar>
      <DropdownToggle nav className={activeClass}>
        Support
      </DropdownToggle>
      <DropdownMenu right>
        <Link href='/support/faqs' passHref>
          <DropdownItem tag='a'>FAQ</DropdownItem>
        </Link>
        <Link href='/support/contacts' passHref>
          <DropdownItem tag='a'>Contacts</DropdownItem>
        </Link>
        <Link href='/support/office-locations' passHref>
          <DropdownItem tag='a'>Office Locations</DropdownItem>
        </Link>
        {origamiPermission &&
          <Link href='/support/report-incident' passHref>
            <DropdownItem tag='a'>Report An Incident</DropdownItem>
          </Link>}
        <SupportNavDropdownApps profile={profile} />
      </DropdownMenu>
    </UncontrolledDropdown>
  )
}

SupportNavDropdown.propTypes = {
  pathname: PropTypes.string.isRequired,
  profile: PropTypes.object.isRequired
}

function NavDivider () {
  return <Nav className='nav-divider d-none d-md-flex' />
}

function NavIcons ({ admin, profile, toggleSearchModal, toggleQuickLinksModal }) {
  return (
    <Nav className='nav-icons'>
      <NavItem>
        <NavLink onClick={toggleSearchModal}>
          <i className='fa fa-search' />
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink onClick={toggleQuickLinksModal}>
          <i className='fa fa-language' />
        </NavLink>
      </NavItem>
      <Notifications profile={profile} />
      <OutageNotifications />
      {admin &&
        <NavItem>
          <Link href='/admin' passHref>
            <NavLink>
              <i className='fa fa-settings-outline' />
            </NavLink>
          </Link>
        </NavItem>}
    </Nav>
  )
}

function NavProfile ({ employeeId, name, provider }) {
  return (
    <Nav className='nav-profile d-none d-md-flex'>
      <UncontrolledDropdown nav inNavbar>
        <DropdownToggle nav caret>
          <PersonCircle
            size={40}
            imageUrl={`https://cdn.myscphealth.com/user-photos/employee-${employeeId}.png`}
            className='d-inline-block'
          />
          <div className='nav-profile-name'>{name}</div>
        </DropdownToggle>
        <DropdownMenu right>
          <DropdownItem className='dropdown-item-name' disabled>{name}</DropdownItem>
          <Link href='/profile' passHref>
            <DropdownItem tag='a' hidden={!provider}>
              <i className='fa fa-user-md fa-lg' />
              Profile
            </DropdownItem>
          </Link>
          <DropdownItem tag='a' href={`${process.env.API_BASE_URL}/logout`}>
            <i className='fa fa-logout fa-lg' />
            Log out
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
    </Nav>
  )
}

function TopNavPublic () {
  return (
    <Container className='px-3' fluid>
      <Navbar className='topnav'>
        <NavImage />
      </Navbar>
    </Container>
  )
}

function TopNavAuthenticated ({ url, profile, admin, provider, toggleSideNav, toggleSearchModal, toggleQuickLinksModal }) {
  return (
    <Container className='px-3' fluid>
      <Navbar className='topnav'>
        <NavToggler toggleSideNav={toggleSideNav} />
        <NavImageLink>
          <NavImage />
        </NavImageLink>
        <NavMain url={url} profile={profile} />
        <NavDivider />
        <NavIcons admin={admin} profile={profile} toggleSearchModal={toggleSearchModal} toggleQuickLinksModal={toggleQuickLinksModal} />
        <NavDivider />
        <NavProfile name={profile.user.fullName} employeeId={profile.user.employeeId} provider={provider} />
      </Navbar>
    </Container>
  )
}

function TopNav ({ url, publicAccess, profile, admin, provider, toggleSideNav, toggleSearchModal, toggleQuickLinksModal }) {
  if (publicAccess) {
    return (
      <TopNavPublic />
    )
  } else {
    return (
      <TopNavAuthenticated
        url={url}
        profile={profile}
        admin={admin}
        provider={provider}
        toggleSideNav={toggleSideNav}
        toggleSearchModal={toggleSearchModal}
        toggleQuickLinksModal={toggleQuickLinksModal}
      />
    )
  }
}

TopNav.propTypes = {
  url: PropTypes.object,
  publicAccess: PropTypes.bool.isRequired,
  profile: PropTypes.object,
  admin: PropTypes.bool,
  provider: PropTypes.bool,
  toggleSideNav: PropTypes.func,
  toggleSearchModal: PropTypes.func,
  toggleQuickLinksModal: PropTypes.func
}

export default TopNav
